import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  ShieldCheck,
  MessagesSquare,
  LayoutGrid,
  MapPin,
  Star,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { CTAButton } from "@/components/CTAButton";
import { Reveal, MaskedHeading, FadeIn } from "@/components/motion";
import {
  Section,
  BrandSection,
  CTABanner,
  InfoGrid,
} from "@/components/Sections";
import { BUSINESS, SERVICES, IMAGES, img } from "@/lib/site";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const WHY = [
  { icon: "ShieldCheck", title: "Professional Workmanship", text: "Careful installation and service work with attention to the details that determine the final result." },
  { icon: "MessagesSquare", title: "Clear Communication", text: "Straightforward explanations, practical recommendations and no unnecessary confusion." },
  { icon: "LayoutGrid", title: "Practical Solutions", text: "We inspect the system, identify the problem and recommend the most sensible repair or next step." },
  { icon: "MapPin", title: "Local Service", text: "Reliable air-conditioning support across Paphos and surrounding areas." },
];
const WHY_ICONS = { ShieldCheck, MessagesSquare, LayoutGrid, MapPin };

const NEARBY = ["Kato Paphos", "Chloraka", "Peyia", "Coral Bay", "Tala", "Tsada", "Geroskipou", "Kissonerga", "Emba", "Mandria", "Anarita"];

const GOOGLE_REVIEWS_URL = "https://share.google/16cASIoEq75Zmd5G5";

const REVIEWS = [
  {
    name: "Kevin Perkins",
    text: "Fantastic service from a professional team. Excellent communication, fast response and a very high standard of work. We have another two units to replace and will definitely use NorthWork Cyprus again.",
  },
  {
    name: "James Gardner",
    text: "Excellent service, really friendly guys and super hard working. Our old unit stopped working and they installed a new one the same day. Thank goodness, we're cool again!",
  },
  {
    name: "David Morris",
    text: "We've had AC units for over 20 years and this was by far the most thorough clean we've ever had. They diagnosed the fault quickly, deep cleaned all our units and left everything spotless. Extremely impressive.",
  },
  {
    name: "Lauren Jeavons",
    text: "Absolutely amazing service. They removed four old units and installed four new inverter ACs, working until almost 9pm to finish the job. Fast, tidy and incredibly hardworking. Highly recommended.",
  },
  {
    name: "Darran Lofthouse",
    text: "Very clear upfront pricing and an extremely clean installation. Old holes were filled, everything was left tidy and all rubbish was removed. We will certainly use NorthWork Cyprus again.",
  },
  {
    name: "Caroline Meechan",
    text: "George and his colleague cleaned our AC units and were brilliant. The work was clean and efficient and they left the place immaculate. We will definitely use them again.",
  },
  {
    name: "Lilyana Bratanova",
    text: "Very fast and reliable. Amazing job cleaning my ACs — afterwards the whole house smelled fresh and everything was cleaned up perfectly. Highly recommended.",
  },
  {
    name: "Jim Hardacre",
    text: "After being let down twice by others, NorthWork came the same evening. They diagnosed the fault, replaced the pipe, cleaned the system and fixed the flooding. Totally professional service.",
  },
  {
    name: "Peter Waddingham",
    text: "George and his team installed four new units in no time. The house is now quiet with perfect temperatures throughout. An excellent job and we would thoroughly recommend NorthWork.",
  },
  {
    name: "Niki Ellis",
    text: "Excellent service from NorthWork Cyprus! George and his team replaced two old air conditioners and installed a completely new one. They were incredibly fast, efficient and professional. Everything was done neatly and cleanly, with great attention to detail. I'm extremely happy with their work and would highly recommend them. Thank you, George and the team!",
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="NorthWork Cyprus | Professional Air Conditioning Services in Paphos"
        description="Professional air conditioning services in Paphos, Cyprus — AC installation, deep cleaning, repair, diagnostics and gas refill for homes, apartments and businesses."
        path="/"
      />

      <Hero />
      <ServicesSection />
      <IntroSection />
      <WhySection />

      <Section className="border-t border-border py-24 lg:py-32">
        <div className="container-x">
          <BrandSection />
        </div>
      </Section>

      <ReviewsSection />
      <ServiceArea />

      <CTABanner
        title="Need Air Conditioning Service?"
        text="Contact NorthWork Cyprus for installation, deep cleaning, repair, diagnostics or gas refill services across Paphos."
        primaryLabel="Contact NorthWork"
        primaryTo="/contact"
        image="/images/cta.webp"
      />
    </>
  );
}

function Hero() {
  return (
    <section
      data-testid="home-hero"
      className="relative -mt-[72px] flex min-h-[520px] items-center overflow-hidden border-b border-border bg-charcoal sm:min-h-[620px] lg:min-h-[760px]"
    >
      {/* Full-bleed background video */}
      <div className="absolute inset-0">
        <video
          className="h-full w-full object-cover object-[80%_30%] sm:object-center"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/media/hero-poster.jpg"
          aria-hidden="true"
        >
          <source src="/media/hero.mp4" type="video/mp4" />
        </video>
        {/* Overlay — stronger behind the text (left/bottom), lighter over the AC (upper-right) */}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/85 via-charcoal/55 to-charcoal/10" />
        <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/45 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="container-x relative grid w-full pb-12 pt-24 lg:grid-cols-12 lg:pb-24 lg:pt-32">
        <div className="flex flex-col justify-center lg:col-span-7">
          <h1 className="font-heading text-[2.4rem] font-medium leading-[1.0] tracking-tight text-white sm:text-6xl lg:text-[5rem]">
            <MaskedHeading
              lines={[
                "Professional",
                "Air Conditioning",
                { text: "Services in Paphos", className: "text-brand" },
              ]}
              startDelay={0.1}
            />
          </h1>

          <FadeIn delay={0.42} variant="up" className="mt-6 max-w-xl lg:mt-8">
            <p className="text-base leading-relaxed text-white/75 lg:text-lg">
              Installation, deep cleaning, diagnostics, repair and gas refill for homes,
              apartments and businesses across Paphos and surrounding areas.
            </p>
          </FadeIn>

          <FadeIn delay={0.54} variant="up" className="mt-6 flex flex-col gap-3 sm:flex-row lg:mt-9">
            <CTAButton
              onClick={() => scrollToId("services")}
              testid="hero-explore-cta"
              className="py-3 lg:py-4"
            >
              Explore Our Services
            </CTAButton>
            <CTAButton
              href={`tel:${BUSINESS.phoneTel}`}
              variant="outlineLight"
              icon={Phone}
              testid="hero-call-cta"
              className="py-3 lg:py-4"
            >
              {BUSINESS.phoneDisplay}
            </CTAButton>
          </FadeIn>

          <FadeIn delay={0.66} variant="up" className="mt-7 lg:mt-10">
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono-t text-[11px] uppercase tracking-[0.16em] text-white/60">
              <span>Professional workmanship</span>
              <span className="text-brand">·</span>
              <span>Clear service</span>
              <span className="text-brand">·</span>
              <span>Reliable local support</span>
            </p>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <Section id="services" testid="home-services" className="pt-16 pb-24 lg:pt-20 lg:pb-32">
      <div className="container-x">
        <div className="max-w-2xl">
          <Reveal delay={0.05} variant="up">
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Our Services
            </h2>
          </Reveal>
          <Reveal delay={0.1} variant="up" className="mt-4">
            <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">
              Professional air conditioning installation, deep cleaning, diagnostics, repairs and gas
              refill for homes and businesses across Paphos.
            </p>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:mt-12">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.key} service={s} index={i} />
          ))}
        </div>
      </div>
    </Section>
  );
}

function ServiceCard({ service, index }) {
  return (
    <Reveal delay={(index % 2) * 0.08} variant={index % 2 === 0 ? "left" : "right"}>
      <Link
        to={service.path}
        data-testid={`service-card-${service.key}`}
        className="group relative block aspect-[4/3] overflow-hidden bg-charcoal"
      >
        <img
          src={img(service.image, 900)}
          alt={service.alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-charcoal/5" />
        <div className="absolute inset-0 flex flex-col justify-end p-7 lg:p-9">
          <h3 className="font-heading text-2xl font-semibold text-white lg:text-[1.7rem]">
            {service.title}
          </h3>
          <p className="mt-3 max-w-md text-[14px] leading-relaxed text-white/70">{service.short}</p>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white">
            {service.button}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

function IntroSection() {
  return (
    <Section testid="home-intro" className="border-t border-border bg-surface py-24 lg:py-32">
      <div className="container-x">
        <div className="mx-auto max-w-[960px] text-center">
          <Reveal delay={0.05} variant="up">
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Professional Workmanship. Clear Service.
            </h2>
          </Reveal>
          <Reveal delay={0.1} variant="up" className="mx-auto mt-6 max-w-3xl space-y-5 text-base leading-relaxed text-charcoal-muted lg:text-lg">
            <p>
              NorthWork Cyprus provides professional air-conditioning services with a focus on
              careful workmanship, clear communication and reliable service.
            </p>
            <p>
              From the first inspection to completion, we take a practical approach — identify the
              issue, explain the options clearly and carry out the work properly.
            </p>
          </Reveal>
          <Reveal delay={0.18} variant="up" className="mt-8 flex justify-center">
            <CTAButton to="/about" variant="secondary" testid="intro-about-cta">
              About NorthWork
            </CTAButton>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

function WhySection() {
  return (
    <Section testid="home-why" className="py-24 lg:py-32">
      <div className="container-x">
        <div className="max-w-2xl">
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Why Choose NorthWork Cyprus
            </h2>
          </Reveal>
        </div>
        <div className="mt-10 lg:mt-12">
          <InfoGrid items={WHY} columns={4} iconMap={WHY_ICONS} hover testid="why-grid" />
        </div>
      </div>
    </Section>
  );
}

function GoogleG({ className }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

// Review body: collapsed to ~3 lines with a smooth "Read more" expansion.
function ReviewText({ text }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (el) setOverflows(el.scrollHeight > el.clientHeight + 12);
  }, [text]);

  return (
    <div className="mt-6">
      <div
        className="grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ gridTemplateRows: expanded ? "1fr" : "4.875em" }}
      >
        <p
          ref={ref}
          className="overflow-hidden text-base leading-relaxed text-charcoal sm:text-lg"
        >
          &ldquo;{text}&rdquo;
        </p>
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          data-testid="review-read-more"
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 text-sm font-medium text-brand transition-colors duration-300 hover:text-brand-hover"
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
}

function ReviewsSection() {
  const [active, setActive] = useState(0);
  const count = REVIEWS.length;
  const go = useCallback((dir) => setActive((a) => (a + dir + count) % count), [count]);

  // Auto-advance, paused is not required; gentle rotation every 7s.
  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % count), 7000);
    return () => clearInterval(t);
  }, [count]);

  const review = REVIEWS[active];

  return (
    <Section testid="home-reviews" className="border-t border-border pt-14 pb-20 lg:pt-16 lg:pb-24">
      <div className="container-x">
        {/* Heading */}
        <div className="mx-auto max-w-2xl text-center">
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              What Our Customers Say
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-4">
            <p className="inline-flex flex-wrap items-center justify-center gap-2 text-sm font-medium text-charcoal">
              <GoogleG className="h-5 w-5 shrink-0" />
              <span>99+ Google Reviews</span>
              <span className="text-charcoal-muted">·</span>
              <span className="inline-flex items-center gap-1">
                5.0 Rating
                <span className="flex gap-0.5 text-gold">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </span>
              </span>
            </p>
          </Reveal>
        </div>

        {/* Carousel */}
        <div className="mx-auto mt-12 flex max-w-4xl items-center gap-3 sm:gap-6">
          <button
            type="button"
            aria-label="Previous review"
            data-testid="review-prev"
            onClick={() => go(-1)}
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-white text-charcoal transition-all duration-300 hover:border-brand hover:text-brand sm:inline-flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <div className="relative flex-1">
            <div
              key={active}
              data-testid={`review-card-${active}`}
              className="reveal rounded-3xl border border-border bg-white p-8 shadow-[0_30px_60px_-40px_rgba(15,23,42,0.35)] sm:p-10 lg:p-12"
            >
              <div className="flex items-start justify-between gap-4">
                <span className="flex gap-1 text-gold">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-current" />
                  ))}
                </span>
                <GoogleG className="h-6 w-6 shrink-0" />
              </div>
              <ReviewText text={review.text} />
              <div className="mt-8 border-t border-border pt-5">
                <p className="text-base font-semibold text-charcoal">{review.name}</p>
                <p className="mt-1 font-mono-t text-[11px] uppercase tracking-[0.16em] text-charcoal-muted">
                  Google Review
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            aria-label="Next review"
            data-testid="review-next"
            onClick={() => go(1)}
            className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-white text-charcoal transition-all duration-300 hover:border-brand hover:text-brand sm:inline-flex"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Mobile arrows + dots */}
        <div className="mx-auto mt-8 flex max-w-4xl items-center justify-center gap-5">
          <button
            type="button"
            aria-label="Previous review"
            onClick={() => go(-1)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-charcoal transition-colors duration-300 hover:border-brand hover:text-brand sm:hidden"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2.5" data-testid="review-dots">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to review ${i + 1}`}
                data-testid={`review-dot-${i}`}
                onClick={() => setActive(i)}
                className={
                  "h-2 rounded-full transition-all duration-300 " +
                  (i === active ? "w-6 bg-brand" : "w-2 bg-charcoal/20 hover:bg-charcoal/40")
                }
              />
            ))}
          </div>

          <button
            type="button"
            aria-label="Next review"
            onClick={() => go(1)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white text-charcoal transition-colors duration-300 hover:border-brand hover:text-brand sm:hidden"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Read all */}
        <div className="mt-10 flex justify-center">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-cta"
            className="group inline-flex items-center gap-2 text-sm font-medium text-charcoal transition-colors duration-300 hover:text-brand"
          >
            Read All Google Reviews
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </Section>
  );
}

function ServiceArea() {
  return (
    <Section testid="home-area" className="border-t border-border bg-surface py-20 lg:py-24">
      <div className="container-x grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal delay={0.05}>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Air Conditioning Services Across Paphos
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-5">
            <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">
              Professional air-conditioning services across Paphos and surrounding communities.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-2.5">
            {NEARBY.map((area) => (
              <span
                key={area}
                className="cursor-default rounded-full border border-border bg-white px-4 py-2 text-[13px] text-charcoal-muted transition-colors duration-300 hover:border-brand hover:bg-brand/[0.04] hover:text-brand"
              >
                {area}
              </span>
            ))}
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7">
          <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-white">
            <img
              src="/images/paphos-coast.webp"
              alt="Paphos coastline and harbour promenade on the Mediterranean, Cyprus"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <span className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 font-mono-t text-[11px] uppercase tracking-[0.16em] text-charcoal backdrop-blur">
              <MapPin className="h-3.5 w-3.5 text-brand" /> Paphos · Cyprus
            </span>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
